import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { DepartmentsFilterOption } from '../models/department-filter-option';
import { DepartmentOrderingOptions, DepartmentRegisterOptions } from '../models/department-register-options';
import { getDepartmentQuery } from '../queries/GetDepartments.graphql';
import { GetDepartment, GetDepartmentVariables } from '../queries/generated/GetDepartment';
import { GetDepartmentNames, GetDepartmentNamesVariables } from '../queries/generated/GetDepartmentNames';
import { getDepartmentNamesQuery } from '../queries/getDepartmentNames.graphql';

@Injectable({ providedIn: 'root' })
export class DepartmentRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getDepartment(
      endCursor: string | null,
      filters: DepartmentsFilterOption[] | null,
      quickFilters: DepartmentRegisterOptions | null,
      orderingOptions: DepartmentOrderingOptions[] | null
   ): Observable<ExecutionResult<GetDepartment>> {
      const filterBuilderFilterExpression: FilterExpression = { filterOptions: [] };

      if (filters && filters.length > 0) {
         filterBuilderFilterExpression.filterOptions = filters.map(this.convertLocationFilterOption.bind(this));
      }

      if (quickFilters && quickFilters.showAllDepartments) {
         filterBuilderFilterExpression.filterOptions =
            filterBuilderFilterExpression.filterOptions?.filter((filter) => filter.field !== 'IsActive') ?? [];
      }

      if (quickFilters && quickFilters.searchText) {
         const searchWords = quickFilters.searchText.trim().split(/\s+/);
         const fieldsToSearch = ['Name'];

         filterBuilderFilterExpression.subExpressions = searchWords.map((word) => {
            const wordSubExpression: FilterExpression = {
               filterOptions: fieldsToSearch.map((field) => {
                  return {
                     field: field,
                     operation: FilterOperations.Contains,
                     value: word,
                  };
               }),
               logicalOperator: LogicalOperators.Or,
            };

            return wordSubExpression;
         });

         filterBuilderFilterExpression.logicalOperator = LogicalOperators.And;
      }
      const variables = this.getDepartmentVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetDepartment, GetDepartmentVariables>({
         query: getDepartmentQuery,
         variables,
      });
   }

   getDepartmentNames(accountId: number | null): Observable<ExecutionResult<GetDepartmentNames>> {
      const filterBuilderFilterExpression: FilterExpression | null = { filterOptions: [] };
      filterBuilderFilterExpression.filterOptions.push({
         field: 'AccountId',
         operation: FilterOperations.EqualTo,
         value: accountId,
      });
      const variables = this.getDepartmentNamesVariables(null, filterBuilderFilterExpression, null);
      return this.apiService.query<GetDepartmentNames, GetDepartmentNamesVariables>({
         query: getDepartmentNamesQuery,
         variables,
      });
   }

   private getDepartmentNamesVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetDepartmentNamesVariables {
      const variables: GetDepartmentNamesVariables = {
         after,
         filtersDepartments: this.buildFilterExpression(filterBuilderFilters),
         orderingOptions,
      };

      return variables;
   }

   private convertLocationFilterOption(option: DepartmentsFilterOption): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(filterOption: DepartmentsFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (filterOption.operation === FilterOperations.HasValue) {
         return filterOption.value;
      }

      switch (filterOption.type) {
         case 'department':
            return Array.isArray(filterOption.value)
               ? filterOption.value.map((v) => {
                    return v.departmentId;
                 })
               : filterOption.value?.departmentId || null;

         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return filterOption.value;
      }
   }

   private getDepartmentVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetDepartmentVariables {
      const variables: GetDepartmentVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }
}
